import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, CircleDashed, Download, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { analystApi, downloadRunExport, type RunEventView, type RunView } from '../../services/api';
import { ReportView } from './ReportView';
import { SignalBadge } from './RefText';

/**
 * Una domanda del thread (§13.1): avanzamento dagli eventi del run, card di chiarimento,
 * report finale e azioni. Il polling si ferma quando il run termina o attende l'utente.
 */

const PHASE_LABEL: Record<string, string> = {
  QUEUED: 'In coda', INTAKE: 'Lettura della domanda', NEEDS_INPUT: 'In attesa della tua risposta', SCOPING: 'Verifica del perimetro',
  ACQUIRING: 'Scarico dei dati', PREPARING: 'Preparazione dei dati', COMPUTING: 'Calcolo delle metriche', ENRICHING: 'Analisi aggiuntive',
  EXPLORING: 'Lettura dei dati', DRAFTING: 'Stesura del report', VERIFYING: 'Verifica', REPORTING_LIMITS: 'Limiti dei dati',
  COMPLETED: 'Completato', FAILED: 'Interrotto', CANCELLED: 'Annullato',
};

const ACTIVE = new Set(['QUEUED', 'INTAKE', 'SCOPING', 'ACQUIRING', 'PREPARING', 'COMPUTING', 'ENRICHING', 'EXPLORING', 'DRAFTING', 'VERIFYING', 'REPORTING_LIMITS']);

interface RunCardProps {
  runId: string;
  /** Il run è terminato o ha cambiato stato: la pagina aggiorna l'elenco. */
  onChanged: () => void;
  /** Nuova domanda creata da "rigenera". */
  onNewRun: (runId: string) => void;
}

export function RunCard({ runId, onChanged, onNewRun }: RunCardProps) {
  const [run, setRun] = useState<RunView | null>(null);
  const [events, setEvents] = useState<RunEventView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lastEvent = useRef(0);
  const lastStatus = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [r, ev] = await Promise.all([analystApi.run(runId), analystApi.events(runId, lastEvent.current)]);
      if (ev.length) {
        lastEvent.current = ev[ev.length - 1]?.id ?? lastEvent.current;
        setEvents((prev) => [...prev, ...ev]);
      }
      setRun(r);
      setError(null);
      if (lastStatus.current !== null && lastStatus.current !== r.status) onChanged();
      lastStatus.current = r.status;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [runId, onChanged]);

  useEffect(() => {
    lastEvent.current = 0;
    lastStatus.current = null;
    setEvents([]);
    setRun(null);
    void refresh();
  }, [runId, refresh]);

  useEffect(() => {
    if (!run || !ACTIVE.has(run.status)) return;
    const t = setInterval(() => { void refresh(); }, 3000);
    return () => clearInterval(t);
  }, [run, refresh]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!run) {
    return <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  }
  const active = ACTIVE.has(run.status);
  const datasetId = run.datasetIds?.[0] ?? (run.scope.primary.kind === 'dataset' ? run.scope.primary.datasetId : null);

  return (
    <article className="space-y-3">
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-blue-900/40 border border-blue-800 rounded-lg px-3 py-2 text-sm text-blue-50 whitespace-pre-wrap">{run.prompt}</div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs">
          {active && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
          {run.status === 'COMPLETED' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          {(run.status === 'FAILED' || run.status === 'CANCELLED') && <XCircle className="w-4 h-4 text-red-400" />}
          {run.status === 'NEEDS_INPUT' && <CircleDashed className="w-4 h-4 text-amber-400" />}
          <span className="font-semibold text-gray-200">{PHASE_LABEL[run.status] ?? run.status}</span>
          {run.status === 'COMPLETED' && <SignalBadge signal={run.signal} />}
          {run.plan && <span className="text-gray-500">· {run.plan.intent} · {run.plan.units}</span>}
        </div>

        {(active || run.status === 'NEEDS_INPUT' || run.status === 'FAILED') && (
          <ol className="text-xs text-gray-400 space-y-0.5 max-h-48 overflow-y-auto">
            {events.map((e) => (
              <li key={e.id} className={e.level === 'error' ? 'text-red-300' : e.level === 'warning' ? 'text-amber-300' : ''}>
                <span className="text-gray-600 mr-2">{e.ts.slice(11, 19)}</span>{PHASE_LABEL[e.phase] ?? e.phase}: {e.message}
              </li>
            ))}
          </ol>
        )}

        {run.status === 'NEEDS_INPUT' && run.pending && (
          <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-3 space-y-2">
            <p className="text-sm text-amber-50">{run.pending.question}</p>
            <div className="flex flex-wrap gap-2">
              {run.pending.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  disabled={busy}
                  onClick={() => act(() => analystApi.answer(run.id, o.id))}
                  className="text-xs px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-white disabled:opacity-50"
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-amber-200/70">"Cambia" chiude questa domanda: scegli il nuovo perimetro qui sotto e chiedi di nuovo.</p>
          </div>
        )}

        {run.status === 'FAILED' && run.error?.message && <p className="text-xs text-red-300">{run.error.message}</p>}
        {run.report && <ReportView report={run.report} datasetId={datasetId} />}
        {error && <p className="text-xs text-red-300">{error}</p>}

        <div className="flex flex-wrap gap-2 pt-1">
          {active && (
            <button type="button" disabled={busy} onClick={() => act(() => analystApi.cancel(run.id))} className="text-xs px-3 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-50">
              Annulla
            </button>
          )}
          {run.status === 'COMPLETED' && (
            <>
              <button type="button" onClick={() => act(() => downloadRunExport(run.id, 'md'))} className="text-xs px-3 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 flex items-center gap-1"><Download className="w-3 h-3" /> Markdown</button>
              <button type="button" onClick={() => act(() => downloadRunExport(run.id, 'json'))} className="text-xs px-3 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 flex items-center gap-1"><Download className="w-3 h-3" /> JSON</button>
            </>
          )}
          {!active && run.status !== 'NEEDS_INPUT' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => act(async () => { const r = await analystApi.rerun(run.id); onNewRun(r.runId); })}
              className="text-xs px-3 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RotateCcw className="w-3 h-3" /> Rigenera con dati aggiornati
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
